/**
 * The at-limit sheet — the single most important screen in the product
 * (PPL-350).
 *
 * The only place where someone wants something, is told no, and can fix it in
 * one step. So it explains what they would get rather than just refusing, and
 * upgrade is the one path offered.
 *
 * `add` is the sixth person; `resume` is un-pausing someone with no room, where
 * the header swaps and one extra line offers the alternative that costs nothing.
 * Resuming is never framed as recovery — their story was archived the whole time.
 *
 * ## Every number is served
 *
 * The people limit, the window length and the archive total all arrive from the
 * backend. Nothing hard-codes 5 or 30, and each line is built so it reads
 * correctly when a number is missing rather than printing a placeholder. `null`
 * never becomes 0.
 *
 * ## The trial line is off until there is a trial
 *
 * The design carries "you've had this before — your first 14 days ran on full
 * memory", which is the reverse trial (PPL-357) and does not exist. Shown now it
 * is a false claim about the user's own history, on the one screen where trust
 * does the selling. `hadTrial` defaults false; PPL-357 switches it on.
 *
 * ## Instrumentation
 *
 * `gate_hit` is fired SERVER-side by `setPersonRememberedEurW` when it refuses,
 * so this does not emit it — doing both would double-count the one funnel number
 * the tier decision rests on.
 */

import React, { useEffect } from 'react';
import { Check } from 'react-feather';
import { Sparkle } from './glyphs';
import { RT } from './tokens';
import type { AccountArchive, RememberQuota } from '../../services/rememberService';
import type { LimitMode } from '../../hooks/useRememberPerson';
import { capture } from '../../services/posthog';
import { MonetizationEvents, PaywallGate, PaywallSource } from '../../services/monetizationEvents';

interface Props {
  open: boolean;
  /**
   * Which refusal this is. `add` and `resume` are BREADTH — the slots are gone.
   * `window` is PERMANENCE: an actively-remembered person's older memories, which
   * has nothing to do with slots. Telling someone with two slots free that "every
   * slot is full" is untrue, and it sells them the wrong thing besides.
   */
  mode: LimitMode;
  /** PERSONAL register — the person they were reaching for. */
  firstName: string;
  quota: RememberQuota | null;
  archive?: AccountArchive | null;
  /** PPL-357. False until the reverse trial exists. */
  hadTrial?: boolean;
  onUpgrade: () => void;
  onClose: () => void;
}

export default function UpgradeSheet({
  open, mode, firstName, quota, archive = null, hadTrial = false, onUpgrade, onClose,
}: Props) {
  // Escape closes. A refusal the user cannot dismiss with the key everything
  // else uses reads as a trap, which is the opposite of what this should feel
  // like at the moment it appears.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  const who = firstName || 'them';
  const limit = quota?.limit ?? null;
  const days = quota?.memoryWindowDays ?? null;

  const scope = [
    limit === null ? null : `${limit} ppl`,
    days === null ? null : `${days} days at a time`,
  ].filter(Boolean).join(', ');

  const title = mode === 'add' ? 'every slot is full'
    : mode === 'resume' ? `resuming ${who} needs a slot`
      : `f8's memory of ${who} goes back ${days === null ? 'only so far' : `${days} days`}`;

  // Breadth and permanence are different refusals and must not borrow each
  // other's words: "every slot is full" said to someone with slots free is
  // simply false, and the window is the thing actually in the way.
  const opener = mode === 'window'
    ? (days === null
      ? `everything older is archived — stored, and out of f8's reach on free.`
      : `on free f8 works from ${days} days at a time. everything older is archived — stored, and out of its reach.`)
    : scope
      ? `free lets f8 remember ${scope} — and yours are all spoken for.`
      : 'your slots are all spoken for.';

  const archived = archive?.archived ?? null;
  const archiveDetail = archived === null || archived <= 0
    ? 'everything f8 has ever learned, restored.'
    : `everything f8 has ever learned, restored — ${archived} memories${archive?.truncated ? ' and counting' : ''}.`;

  const benefits: Array<[string, string]> = [
    ['everyone, forever', days === null
      ? 'no slots — every person, the whole story.'
      : `no slots, no ${days}-day window — every person, the whole story.`],
    ['your archive comes back', archiveDetail],
    ['briefs and nudges on every event',
      limit === null ? 'not just the few you picked.' : `not just your ${limit}.`],
  ];

  return (
    <div
      onClick={onClose}
      role="presentation"
      style={{
        position: 'fixed', inset: 0, zIndex: 80,
        background: 'rgba(5,2,10,0.6)', backdropFilter: 'blur(3px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        style={{
          width: 440, maxWidth: '92%', maxHeight: '86vh', overflowY: 'auto',
          borderRadius: 24, background: 'rgba(28,15,40,0.98)',
          border: `1px solid ${RT.borderStrong}`, boxShadow: '0 30px 80px rgba(0,0,0,0.6)',
          padding: '22px 24px 24px',
        }}
      >
        <span style={{
          width: 44, height: 44, borderRadius: 16, display: 'flex',
          alignItems: 'center', justifyContent: 'center', marginBottom: 13,
          background: 'linear-gradient(135deg, #d946ef, #7c3aed)',
          boxShadow: '0 8px 22px rgba(217,70,239,0.4)',
        }}>
          <Sparkle size={20} color="#fff" />
        </span>

        <div style={{ fontSize: 19, fontWeight: 700, color: '#fff', letterSpacing: '-0.02em' }}>
          {title}
        </div>
        <div style={{ fontSize: 12.5, color: RT.muted, marginTop: 6, lineHeight: 1.55 }}>
          {opener}{' '}
          {mode === 'add'
            ? "you've got more ppl worth remembering. premium remembers everyone, forever."
            : mode === 'resume'
              ? `${who}'s story is archived either way — resuming just needs room.`
              : 'premium brings all of it back, and remembers it.'}
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 9, margin: '15px 0' }}>
          {benefits.map(([head, detail]) => (
            <div key={head} style={{
              display: 'flex', gap: 10, alignItems: 'flex-start', padding: '10px 12px',
              borderRadius: 14, background: RT.surfaceSoft, border: `1px solid ${RT.border}`,
            }}>
              <span style={{
                width: 20, height: 20, borderRadius: '50%', flexShrink: 0, marginTop: 1,
                background: 'rgba(217,70,239,0.16)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <Check size={11} color={RT.magenta} />
              </span>
              <span style={{ minWidth: 0 }}>
                <span style={{ display: 'block', fontSize: 12.5, fontWeight: 600, color: '#fff' }}>{head}</span>
                <span style={{ display: 'block', fontSize: 11, color: RT.dim, marginTop: 1, lineHeight: 1.45 }}>{detail}</span>
              </span>
            </div>
          ))}
        </div>

        {hadTrial ? (
          <div style={{ textAlign: 'center', fontSize: 11, color: RT.muted, marginBottom: 9 }}>
            you&apos;ve had this before — your first 14 days ran on full memory.
          </div>
        ) : null}

        <button
          type="button"
          onClick={() => {
            // The click that turns a refusal into intent — the step between
            // `paywall_viewed` (fired when this opened) and the checkout.
            capture(MonetizationEvents.UPGRADE_CTA_CLICKED, {
              source: PaywallSource.REMEMBERED_LIMIT_SHEET,
              gate: PaywallGate.REMEMBERED_PEOPLE,
              mode,
            });
            onUpgrade();
          }}
          style={{
            appearance: 'none', cursor: 'pointer', fontFamily: 'inherit', width: '100%',
            padding: '12px 18px', borderRadius: 999, border: '1px solid transparent',
            background: 'linear-gradient(135deg, #d946ef, #7c3aed)', color: '#fff',
            fontSize: 14, fontWeight: 600, boxShadow: '0 6px 18px rgba(217,70,239,0.28)',
          }}
        >
          start premium
        </button>
        <div style={{ display: 'flex', justifyContent: 'center', marginTop: 6 }}>
          <button
            type="button"
            onClick={onClose}
            style={{
              appearance: 'none', cursor: 'pointer', fontFamily: 'inherit',
              background: 'transparent', border: 0, color: RT.dim,
              fontSize: 12.5, fontWeight: 600, padding: '6px 10px',
            }}
          >
            not now
          </button>
        </div>
        {mode === 'resume' ? (
          <div style={{ textAlign: 'center', fontSize: 10.5, color: RT.dim, marginTop: 2, lineHeight: 1.45 }}>
            or pause one of your {limit ?? 'remembered ppl'} to free the slot — nothing is deleted either way.
          </div>
        ) : null}
      </div>
    </div>
  );
}
